import { baselineTopFillColor1, baselineTopFillColor2, buyColor } from "../../assets/colors";
import React, { useCallback, useContext, useMemo } from "react";
import {
    Box,
    FormGroup,
    Typography,
    FormControl,
    Select,
    MenuItem,
    TextField,
} from "@mui/material";
import { useSelector } from "react-redux";
import { AppContext } from "../../App";
import { selectChartConfig } from "../../selectors/chart";
import ColorSelector from "../colorSelector/colorSelector";
import tinycolor from "tinycolor2";
import { BaselineSeriesOptions } from "../chartV2/customSeries/baselineSeries/options";
import CommonChartSettingsTabs from './commonChartSettingsTabs';
import { chartSettingsFormGroupSx, chartSettingsColorRowLabelSx, chartSettingsColorRowSx, chartSettingsRowSx, chartSettingsSectionTitleSx } from './chartSettingsLayout';

type Props = {
    viewKey: string;
};
const BaselineSettings = ({ viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const primarySeriesGroupId = useMemo(() => {
        return chart?.primarySeriesGroup?.config?.id
    }, [chart?.primarySeriesGroup?.config?.id])

    const priceSettings = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.source ?? "close"
        }
        return "close"

    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])
    
    const handleSourceChange = useCallback((newSource: 'high' | 'close' | 'open' | 'low') => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                source: newSource
            } as BaselineSeriesOptions)
        }
    }, [])
    const baseValue = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.baseValue ?? 50
        }
        return 50
        
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])
    
    const handleBaseValueChange = useCallback((newValue: string) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                baseValue: newValue ? parseFloat(newValue) : 50
            } as BaselineSeriesOptions)
        }
    }, [])

    const topLineColor = useMemo(() => {
        let color = buyColor;
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.topLineColor ?? buyColor
        }
        return tinycolor(color)
    }, [chartConfig, primarySeriesGroupId])

    const bottomLineColor = useMemo(() => {
        let color = '#f23645';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.bottomLineColor ?? '#f23645'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const topFillColor1 = useMemo(() => {
        let color = baselineTopFillColor1;
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.topFillColor1 ?? baselineTopFillColor1
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const topFillColor2 = useMemo(() => {
        let color = baselineTopFillColor2
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.topFillColor2 ?? baselineTopFillColor2
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const bottomFillColor1 = useMemo(() => {
        let color = '#f2364500'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.bottomFillColor1 ?? '#f2364500'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const bottomFillColor2 = useMemo(() => {
        let color = '#f2364547'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.bottomFillColor2 ?? '#f2364547'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleColorChangeSeries = useCallback((newColor: string, type: keyof BaselineSeriesOptions) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                [type]: newColor
            } as Partial<BaselineSeriesOptions>);
        }
    }, [])



    const symbolTabContent = (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                BASELINE
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <Box sx={chartSettingsRowSx}>
                    <Typography sx={{ width: 100, fontSize: "14px", flexShrink: 0 }}>Price Source</Typography>
                    <FormControl size="small">
                        <Select
                            value={priceSettings}
                            onChange={(event) => handleSourceChange(event.target.value as 'open' | 'high' | 'low' | 'close')}
                            sx={{ fontSize: "13px", height: 32, minWidth: 100 }}
                        >
                            <MenuItem value="open">Open</MenuItem>
                            <MenuItem value="high">High</MenuItem>
                            <MenuItem value="low">Low</MenuItem>
                            <MenuItem value="close">Close</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={chartSettingsColorRowSx}>
                    <Typography sx={chartSettingsColorRowLabelSx}>Top Line</Typography>
                    <ColorSelector compact showOnlySwatch color={topLineColor.toHexString()} opacity={topLineColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'topLineColor');
                        }} />
                </Box>
                <Box sx={chartSettingsColorRowSx}>
                    <Typography sx={chartSettingsColorRowLabelSx}>Bottom Line</Typography>
                    <ColorSelector compact showOnlySwatch color={bottomLineColor.toHexString()} opacity={bottomLineColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'bottomLineColor');
                        }} />
                </Box>
                <Box sx={chartSettingsColorRowSx}>
                    <Typography sx={chartSettingsColorRowLabelSx}>Fill Top</Typography>
                    <ColorSelector compact showOnlySwatch color={topFillColor1.toHexString()} opacity={topFillColor1.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'topFillColor1');
                        }} />
                    <ColorSelector compact showOnlySwatch color={topFillColor2.toHexString()} opacity={topFillColor2.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'topFillColor2');
                        }} />
                </Box>
                <Box sx={chartSettingsColorRowSx}>
                    <Typography sx={chartSettingsColorRowLabelSx}>Fill Bottom</Typography>
                    <ColorSelector compact showOnlySwatch color={bottomFillColor1.toHexString()} opacity={bottomFillColor1.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'bottomFillColor1');
                        }} />
                    <ColorSelector compact showOnlySwatch color={bottomFillColor2.toHexString()} opacity={bottomFillColor2.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'bottomFillColor2');
                        }} />
                </Box>
                <Box sx={chartSettingsRowSx}>
                    <Typography sx={{ width: 100, fontSize: "14px", flexShrink: 0 }}>Base Level</Typography>
                    <FormControl size="small" sx={{ width: 83 }}>
                        <TextField
                            fullWidth={false}
                            variant="outlined"
                            size="small"
                            value={baseValue}
                            onChange={(event) => handleBaseValueChange(event.target.value)}
                            type="number"
                        />
                    </FormControl>
                    <Typography sx={{ fontSize: "14px" }}>%</Typography>
                </Box>
            </FormGroup>
        </Box>
    )

    return <CommonChartSettingsTabs viewKey={viewKey} symbolTabContent={symbolTabContent} />
}

export default BaselineSettings;
